import {
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  Timestamp,
} from "firebase/firestore"
import { doc, setDoc, where, collection, updateDoc } from "firebase/firestore"
import dexie, { ensureDb } from "@/database"
import { getUid } from "@/services/auth"

export type Narrative = {
  id: string
  author: string
  comment: string
  modified: Timestamp
  values: { name: string; value: string }[]
}
/**
 * Gets narratives created by specified author
 * which can be either the current inspector uid
 * or admin. This function is used to stores them locally.
 */
export async function downloadNarratives(author: string = "admin") {
  await ensureDb()
  const uid = getUid()
  const db = getFirestore()
  const w = where("author", "in", [uid, "admin"])
  const q = query(collection(db, "narratives"), w)

  const snapshot = await getDocs(q)
  if (snapshot.empty) throw new Error("No narratives present")

  await dexie
    .table("narratives")
    .bulkPut(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
}

export async function createNarrative(comment: string) {
  await ensureDb()
  const db_fire = getFirestore()
  const w = where("comment", "==", comment)
  const w2 = where("author", "==", "admin")
  const q = query(collection(db_fire, "narratives"), w, w2)

  const document = doc(collection(db_fire, `narratives`))
  const snapshot = await getDocs(q)

  if (snapshot.empty) return { values: [] } as unknown as Narrative

  const narrative = snapshot.docs[0].data() as Narrative
  narrative.author = getUid()

  await dexie.table("narratives").put({ ...narrative, id: document.id })

  setDoc(document, narrative).catch(console.error)

  return narrative
}
export async function getNarrative(comment: string, author: string = "admin") {
  await ensureDb()
  const db_fire = getFirestore()
  const w = where("comment", "==", comment)
  const w2 = where("author", "==", author)
  const q = query(collection(db_fire, "narratives"), w, w2)

  const narrative: Narrative = await dexie
    .table("narratives")
    .where({ author, comment })
    .first()

  if (narrative) return narrative

  const snapshot = await getDocs(q)

  if (snapshot.empty) return await createNarrative(comment)
  await dexie
    .table("narratives")
    .put({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() })

  return {
    id: snapshot.docs[0].id,
    ...snapshot.docs[0].data(),
  } as Narrative
}

export function onNarrative(
  comment: string,
  fn: (narrative: Narrative) => void,
  onChange: (narrative: Narrative) => void = () => { },
) {
  let isFetching = true
  let unsubscribe = () => { }

  // 1. Immediate local lookup
  ensureDb().then(async () => {
    const local = await dexie
      .table("narratives")
      .where({ comment })
      .first() as Narrative | undefined
    if (local && isFetching) {
      fn(local)
      isFetching = false
    }
  })

  // 2. Setup Firestore listener
  const db_fire = getFirestore()
  const w = where("comment", "==", comment)
  const q = query(collection(db_fire, "narratives"), w)

  unsubscribe = onSnapshot(q, async snapshots => {
    if (snapshots.empty) return

    const snapshot = snapshots.docs[0]
    const data = snapshot.data()
    const narrative = { id: snapshot.id, ...data } as Narrative

    // Timestamp validation
    const local = await dexie.table("narratives").get(narrative.id) as Narrative | undefined
    if (local && local.modified) {
      const localTime = local.modified.toMillis()
      const remoteTime = narrative.modified ? narrative.modified.toMillis() : 0

      // Protect local data: ignore old server versions
      if (localTime >= remoteTime || remoteTime < localTime + 1000) {
        if (isFetching) {
          fn(local)
          isFetching = false
        } else if (!snapshots.metadata.hasPendingWrites && localTime > remoteTime) {
          const document = doc(db_fire, `narratives/${local.id}`)
          updateDoc(document, local as any).catch(console.error)
        }
        return
      }
    }

    // Remote is newer: update local cache
    await dexie.table("narratives").put(narrative)

    if (isFetching) {
      fn(narrative)
      isFetching = false
    } else {
      onChange(narrative)
    }
  })

  return () => {
    isFetching = false
    unsubscribe()
  }
}

export async function updateNarrative(narrative: Narrative) {
  await ensureDb()
  const db_fire = getFirestore()
  const uid = getUid()
  const { id } = narrative
  console.log(id, uid);

  narrative.author = uid
  narrative.modified = Timestamp.now()
  // Save to local IndexedDB
  await dexie.table("narratives").put(narrative)

  // Save to Firestore
  if (id) {
    const document = doc(db_fire, `narratives/${id}`)

    // Use updateDoc for deltas to trigger latency compensation
    updateDoc(document, {
      author: narrative.author,
      modified: narrative.modified,
      values: narrative.values,
      comment: narrative.comment
    }).catch((error: any) => {
      console.warn("Firestore narrative update failed (will be enqueued if syncworker supports it):", error)
      // Fallback: the original codebase used simple setDoc, if we error here we might want to retry
      setDoc(document, narrative, { merge: true }).catch(console.error)
    })
  } else {
    const document = doc(collection(db_fire, "narratives"))
    narrative.id = document.id
    setDoc(document, narrative).catch(console.error)
  }

  return narrative
}
